/**
 * ExecutionBlockRenderer.tsx
 * Renderiza blocos de execução hierárquicos no cockpit (Minha Mesa).
 */
import React, { useCallback, useMemo } from 'react';
import { CheckSquare, Paperclip, Upload } from 'lucide-react';
import type {
  ExecutionBlock,
  FieldBlockConfig,
  ChecklistBlockConfig,
  EvidenceBlockConfig,
} from '../../../types/card';

interface BlockRendererProps {
  blocks: ExecutionBlock[];
  responses: Record<string, unknown>;
  onResponse: (blockId: string, value: unknown) => void;
  readOnly?: boolean;
}

export const ExecutionBlockRenderer: React.FC<BlockRendererProps> = ({
  blocks,
  responses,
  onResponse,
  readOnly = false,
}) => {
  if (blocks.length === 0) return null;

  return (
    <div className="space-y-5">
      {blocks.map((block) => (
        <BlockNode
          key={block.id}
          block={block}
          responses={responses}
          onResponse={onResponse}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
};

// ============================================================================
// BLOCK NODE — dispatch by type
// ============================================================================

const BlockNode: React.FC<{
  block: ExecutionBlock;
  responses: Record<string, unknown>;
  onResponse: (blockId: string, value: unknown) => void;
  readOnly: boolean;
}> = ({ block, responses, onResponse, readOnly }) => {
  switch (block.type) {
    case 'group':
      return (
        <GroupBlock block={block} responses={responses} onResponse={onResponse} readOnly={readOnly} />
      );
    case 'field':
      return (
        <FieldBlock block={block} responses={responses} onResponse={onResponse} readOnly={readOnly} />
      );
    case 'checklist':
      return (
        <ChecklistBlock block={block} responses={responses} onResponse={onResponse} readOnly={readOnly} />
      );
    case 'evidence':
      return (
        <EvidenceBlock block={block} responses={responses} onResponse={onResponse} readOnly={readOnly} />
      );
    case 'rule':
      return null; // rules are evaluated by engine, not rendered
    default:
      return null;
  }
};

// ============================================================================
// GROUP BLOCK
// ============================================================================

const GroupBlock: React.FC<{
  block: ExecutionBlock;
  responses: Record<string, unknown>;
  onResponse: (blockId: string, value: unknown) => void;
  readOnly: boolean;
}> = ({ block, responses, onResponse, readOnly }) => {
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white/[0.02] dark:bg-white/[0.02]">
      {block.label && !/^novo\s*grupo$/i.test(block.label.trim()) && (
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {block.label}
          </span>
          {(block.config as any).description && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
              {(block.config as any).description}
            </p>
          )}
        </div>
      )}
      {block.label && /^novo\s*grupo$/i.test(block.label.trim()) && (block.config as any).description && (
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {(block.config as any).description}
          </span>
        </div>
      )}
      <div className="p-3 space-y-4">
        {(block.children ?? []).map((child) => (
          <BlockNode
            key={child.id}
            block={child}
            responses={responses}
            onResponse={onResponse}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// FIELD BLOCK
// ============================================================================

const inputClass =
  'w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';

const FieldBlock: React.FC<{
  block: ExecutionBlock;
  responses: Record<string, unknown>;
  onResponse: (blockId: string, value: unknown) => void;
  readOnly: boolean;
}> = ({ block, responses, onResponse, readOnly }) => {
  const cfg = block.config as FieldBlockConfig;
  const value = responses[block.id] ?? cfg.defaultValue ?? '';
  const isEmpty = value === '' || value === undefined || value === null;
  const errorBorder = cfg.required && isEmpty && !readOnly ? ' !border-red-400 dark:!border-red-500' : '';

  const handleChange = useCallback(
    (v: unknown) => onResponse(block.id, v),
    [block.id, onResponse],
  );

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {block.label}
        {cfg.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {cfg.description && (
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-1">{cfg.description}</p>
      )}

      {cfg.fieldType === 'text' && (
        <input
          type="text"
          value={String(value)}
          onChange={(e) => handleChange(e.target.value)}
          disabled={readOnly}
          placeholder={cfg.placeholder}
          className={inputClass + errorBorder}
        />
      )}

      {cfg.fieldType === 'number' && (
        <input
          type="number"
          value={value === '' ? '' : Number(value)}
          onChange={(e) => handleChange(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={readOnly}
          placeholder={cfg.placeholder}
          min={cfg.validation?.min}
          max={cfg.validation?.max}
          className={inputClass + errorBorder}
        />
      )}

      {cfg.fieldType === 'currency' && (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
          <input
            type="number"
            step="0.01"
            value={value === '' ? '' : Number(value)}
            onChange={(e) => handleChange(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={readOnly}
            placeholder={cfg.placeholder}
            className={`${inputClass}${errorBorder} pl-9`}
          />
        </div>
      )}

      {cfg.fieldType === 'date' && (
        <input
          type="date"
          value={String(value)}
          onChange={(e) => handleChange(e.target.value)}
          disabled={readOnly}
          className={inputClass + errorBorder}
        />
      )}

      {cfg.fieldType === 'textarea' && (
        <textarea
          value={String(value)}
          onChange={(e) => handleChange(e.target.value)}
          disabled={readOnly}
          rows={3}
          placeholder={cfg.placeholder}
          className={inputClass + errorBorder}
        />
      )}

      {cfg.fieldType === 'select' && (
        <select
          value={String(value)}
          onChange={(e) => handleChange(e.target.value)}
          disabled={readOnly}
          className={inputClass + errorBorder}
        >
          <option value="">Selecione...</option>
          {(cfg.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}

      {cfg.fieldType === 'checkbox' && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => handleChange(e.target.checked)}
            disabled={readOnly}
            className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 rounded"
          />
          <span className="text-sm text-slate-600 dark:text-slate-400">Sim</span>
        </label>
      )}

      {cfg.fieldType === 'attachment' && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Paperclip size={14} />
          <span>Anexo gerenciado via evidência</span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CHECKLIST BLOCK
// ============================================================================

const ChecklistBlock: React.FC<{
  block: ExecutionBlock;
  responses: Record<string, unknown>;
  onResponse: (blockId: string, value: unknown) => void;
  readOnly: boolean;
}> = ({ block, responses, onResponse, readOnly }) => {
  const cfg = block.config as ChecklistBlockConfig;
  const state = (responses[block.id] ?? {}) as Record<string, boolean>;

  const handleToggle = useCallback(
    (itemId: string) => {
      const next = { ...state, [itemId]: !state[itemId] };
      onResponse(block.id, next);
    },
    [block.id, state, onResponse],
  );

  const completedCount = cfg.items.filter((i) => state[i.id]).length;
  const totalRequired = cfg.items.filter((i) => i.required).length;
  const completedRequired = cfg.items.filter((i) => i.required && state[i.id]).length;

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white/[0.02]">
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <CheckSquare size={12} className="text-slate-400" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {block.label}
          </span>
        </div>
        <span className="text-[10px] font-mono text-slate-400">
          {completedCount}/{cfg.items.length}
          {totalRequired > 0 && completedRequired < totalRequired && (
            <span className="text-red-400 ml-1">({totalRequired - completedRequired} obr.)</span>
          )}
        </span>
      </div>
      <div className="p-3 space-y-2">
        {cfg.items.map((item, idx) => {
          const checked = state[item.id] ?? false;
          const disabled = readOnly || (cfg.requireOrder && idx > 0 && !state[cfg.items[idx - 1].id]);

          return (
            <label
              key={item.id}
              className={`flex items-center gap-2 cursor-pointer ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => !disabled && handleToggle(item.id)}
                disabled={disabled}
                className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 rounded"
              />
              <span className={`text-sm ${checked ? 'text-slate-400 line-through' : 'text-slate-700 dark:text-slate-300'}`}>
                {item.label}
                {item.required && <span className="text-red-500 text-[10px] ml-1">*</span>}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// EVIDENCE BLOCK
// ============================================================================

const EvidenceBlock: React.FC<{
  block: ExecutionBlock;
  responses: Record<string, unknown>;
  onResponse: (blockId: string, value: unknown) => void;
  readOnly: boolean;
}> = ({ block, responses, onResponse, readOnly }) => {
  const cfg = block.config as EvidenceBlockConfig;
  const state = (responses[block.id] ?? { justification: '', files: [] }) as {
    justification?: string;
    files?: string[];
  };

  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white/[0.02]">
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-1.5">
          <Upload size={12} className="text-slate-400" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {block.label}
          </span>
        </div>
      </div>
      <div className="p-3 space-y-3">
        {cfg.requireJustification && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Justificativa / Observação
              <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              value={state.justification ?? ''}
              onChange={(e) =>
                onResponse(block.id, { ...state, justification: e.target.value })
              }
              disabled={readOnly}
              rows={3}
              placeholder="Descreva observações, problemas ou ajustes..."
              className={inputClass}
            />
          </div>
        )}
        {cfg.requireAttachment && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Anexos
              {cfg.requireAttachment && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded p-4 text-center">
              <Upload size={20} className="mx-auto text-slate-400 mb-1" />
              <p className="text-[11px] text-slate-400">
                Arraste ou clique para anexar
                {cfg.allowedTypes?.length ? ` (${cfg.allowedTypes.join(', ')})` : ''}
                {cfg.maxSizeMB ? ` — máx ${cfg.maxSizeMB}MB` : ''}
              </p>
              <input
                type="file"
                disabled={readOnly}
                multiple={cfg.multiple}
                accept={cfg.allowedTypes?.join(',')}
                className="hidden"
                onChange={() => {/* TODO: upload handler */}}
              />
            </div>
          </div>
        )}
        {cfg.requirePhoto && (
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span className="text-[10px]">📷</span>
            <span>Foto obrigatória (disponível no mobile)</span>
          </div>
        )}
      </div>
    </div>
  );
};
